import { Alert, Box, Button, Chip, Divider, Typography } from "@mui/material";
import ContentShell from "components/ContentShell";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import TranslationButtons from "components/TranslationButtons";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import useStyles from "styles/banner/bannerEditor";
import {
  createObjectiveTypeTitle,
  getObjectiveTypeTitles,
  getObjectiveTypeTitleInfo,
  updateObjectiveTypeTitle,
} from "services/objectiveTypeTitleService";
import { getConfigurations } from "services/miscService";
import configData from "config/config.json";
import BulkTranslationUpload from "components/BulkTranslationUpload";

/** Count how many unique {{textN}} placeholders appear in a string */
const countPlaceholders = (text: string): number => {
  if (!text) return 0;
  const matches = text.match(/\{\{text\d+\}\}/g);
  if (!matches) return 0;
  return new Set(matches).size;
};

/** Validate that all placeholders {{text1}}..{{textN}} are sequential */
const getPlaceholderNumbers = (text: string): number[] => {
  if (!text) return [];
  const matches = text.match(/\{\{text(\d+)\}\}/g);
  if (!matches) return [];
  return [...new Set(matches)]
    .map((m) => parseInt(m.replace(/\{\{text|\}\}/g, "")))
    .sort((a, b) => a - b);
};

interface ObjectiveTypeConfig {
  name: string;
  minDynamicValues: number;
  maxDynamicValues: number;
}

interface TitleDetails {
  objectiveType: string;
  titleTemplate: string;
  titleTemplateTrans: Record<string, string>;
}

const _initialDetails: TitleDetails = {
  objectiveType: "",
  titleTemplate: "",
  titleTemplateTrans: {},
};

const ObjectiveTypeTitleEditor = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<TitleDetails>({ ..._initialDetails });
  const [objectiveTypeConfigs, setObjectiveTypeConfigs] = useState<
    ObjectiveTypeConfig[]
  >([]);
  const [usedObjectiveTypes, setUsedObjectiveTypes] = useState<string[]>([]);
  const { isOpen, close, open } = useOpenClose();
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null as string | null,
    viewData: [] as any[],
    modal: "" as string,
  });

  const cancel = () =>
    navigate(configData.routes.challenges.objectiveTypeTitles);

  const selectedConfig = objectiveTypeConfigs.find(
    (c) => c.name === details.objectiveType,
  );

  const objectiveTypeOptions = useMemo(() => {
    return objectiveTypeConfigs
      .filter((c) => {
        const alreadyUsed = usedObjectiveTypes.includes(c.name);
        if (isEdit && c.name === details.objectiveType) return true;
        return !alreadyUsed;
      })
      .map((c) => ({
        id: c.name,
        label: c.name.replace(/_/g, " "),
      }));
  }, [details.objectiveType, isEdit, objectiveTypeConfigs, usedObjectiveTypes]);

  const fetchObjectiveTypeConfigs = async () => {
    try {
      const resp = await getConfigurations();
      if (resp?.data?.length) {
        const otConfig = resp.data.find(
          (c: any) => c.type === "OBJECTIVE_TYPE_CONFIG",
        );
        const configs: ObjectiveTypeConfig[] =
          otConfig?.data?.objectiveTypes || [];
        setObjectiveTypeConfigs(configs);
      }
    } catch (error: any) {
      showSnackbar("Failed to load objective type configurations", "error");
    }
  };

  const fetchUsedObjectiveTypes = async () => {
    try {
      const resp = await getObjectiveTypeTitles();
      const used = (resp?.data || [])
        .map((item: any) => item?.objectiveType)
        .filter(Boolean);
      setUsedObjectiveTypes(used);
    } catch (error: any) {
      showSnackbar("Failed to load existing objective type titles", "error");
    }
  };

  /** Validate placeholders in a single template string against its objective type config */
  const validatePlaceholders = (
    text: string,
    config: ObjectiveTypeConfig | undefined,
  ): string | null => {
    if (!config) return null;
    const count = countPlaceholders(text);
    const numbers = getPlaceholderNumbers(text);
    const { minDynamicValues, maxDynamicValues } = config;

    if (count < minDynamicValues) {
      return `This objective type requires at least ${minDynamicValues} placeholder${minDynamicValues > 1 ? "s" : ""} (e.g. ${Array.from({ length: minDynamicValues }, (_, i) => `{{text${i + 1}}}`).join(", ")}). Found ${count}.`;
    }
    if (count > maxDynamicValues) {
      return `This objective type allows at most ${maxDynamicValues} placeholder${maxDynamicValues > 1 ? "s" : ""}. Found ${count}.`;
    }

    // Check sequential numbering
    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] !== i + 1) {
        return `Placeholders must be numbered sequentially starting from {{text1}}. Found gap at {{text${i + 1}}}.`;
      }
    }
    return null;
  };

  /** Validate all translations have correct placeholder count */
  const validateAllTranslations = (): string | null => {
    if (!selectedConfig) return null;
    const trans = details.titleTemplateTrans || {};
    for (const [lang, text] of Object.entries(trans)) {
      const err = validatePlaceholders(text, selectedConfig);
      if (err) return `Translation [${lang}]: ${err}`;
    }
    return null;
  };

  const fetchInfo = async () => {
    setLoading(true);
    try {
      const resp = await getObjectiveTypeTitleInfo(id);
      if (resp?.data) {
        const data = resp.data;
        const titleTemplateTrans = { ...(data.titleTemplateTrans || {}) };
        if (data.titleTemplate && !titleTemplateTrans["en"]) {
          titleTemplateTrans["en"] = data.titleTemplate;
        }
        setDetails({
          objectiveType: data.objectiveType || "",
          titleTemplate: data.titleTemplate || "",
          titleTemplateTrans,
        });
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const submit = async () => {
    try {
      if (!details.objectiveType) {
        showSnackbar("Objective Type is required", "error");
        return;
      }
      if (!details.titleTemplate) {
        showSnackbar("Title Template is required", "error");
        return;
      }
      if (!isEdit && usedObjectiveTypes.includes(details.objectiveType)) {
        showSnackbar(
          "Objective type title already exists. Only one title is allowed per objective type.",
          "error",
        );
        return;
      }

      // Validate English template placeholders
      const templateErr = validatePlaceholders(
        details.titleTemplate,
        selectedConfig,
      );
      if (templateErr) {
        showSnackbar(templateErr, "error");
        return;
      }

      // Validate all language translations
      const transErr = validateAllTranslations();
      if (transErr) {
        showSnackbar(transErr, "error");
        return;
      }

      setLoading(true);

      const payload: any = {
        objectiveType: details.objectiveType,
        titleTemplate: details.titleTemplate,
        titleTemplateTrans: details.titleTemplateTrans,
      };

      if (isEdit) {
        await updateObjectiveTypeTitle(payload, id);
        showSnackbar("Objective type title updated successfully");
      } else {
        await createObjectiveTypeTitle(payload);
        showSnackbar("Objective type title created successfully");
      }
      setLoading(false);
      cancel();
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const getTranslatedData = (field: keyof TitleDetails) => {
    const trans = (details[field] || {}) as Record<string, string>;
    return Object.entries(trans).map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
  };

  const handleOpenModal = (type: string, field: string) => {
    setModalData((state) => {
      if (type === "view") {
        const data = getTranslatedData(field as keyof TitleDetails);
        return {
          ...state,
          viewData: data,
          field,
          modal: type,
        };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleModalChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default language",
        "error",
      );
      return;
    }
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));
    setDetails((prev) => {
      const updated = { ...prev.titleTemplateTrans };
      delete updated[data.lang];
      return { ...prev, titleTemplateTrans: updated };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    if (!modalData.language || !modalData.inputValue) {
      showSnackbar("Language and translation are required", "error");
      return;
    }

    // Validate placeholders in this translation
    const err = validatePlaceholders(modalData.inputValue, selectedConfig);
    if (err) {
      showSnackbar(err, "error");
      return;
    }

    setDetails((prev) => {
      const translatedField = (prev[field as keyof TitleDetails] ||
        {}) as Record<string, string>;
      const updated = {
        ...translatedField,
        [modalData.language]: modalData.inputValue,
      };

      const newDetails = {
        ...prev,
        [field]: updated,
      } as TitleDetails;

      if (modalData.language === "en") {
        newDetails.titleTemplate = modalData.inputValue;
      }
      return newDetails;
    });

    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  useEffect(() => {
    fetchObjectiveTypeConfigs();
    fetchUsedObjectiveTypes();
    if (isEdit) fetchInfo();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  const translationCount = Object.keys(details.titleTemplateTrans || {}).length;

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={
        isEdit ? "Edit Objective Type Title" : "Create Objective Type Title"
      }
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="button"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="button" variant="contained" onClick={submit}>
            {isEdit ? "Update" : "Save"}
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Objective Type"
              name="objectiveType"
              value={details.objectiveType}
              options={objectiveTypeOptions}
              onChange={handleChange}
              disabled={isEdit}
            />
          </Box>

          <Box sx={{ minWidth: 320, flex: 1 }}>
            <SimpleTextInput
              label="Title Template (English)"
              name="titleTemplate"
              value={details.titleTemplate}
              onChange={(e: any) => {
                handleChange(e);
                setDetails((prev) => ({
                  ...prev,
                  titleTemplateTrans: {
                    ...prev.titleTemplateTrans,
                    en: e.target.value,
                  },
                }));
              }}
              button={
                <TranslationButtons
                  field="titleTemplateTrans"
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Active translations
          </Typography>
          <Chip
            size="small"
            label={`${translationCount} language${translationCount === 1 ? "" : "s"}`}
            color={translationCount > 0 ? "primary" : "default"}
          />
          {details.titleTemplateTrans?.en && (
            <Chip size="small" label="English ready" color="success" />
          )}
        </Box>

        <Alert severity="info" sx={{ mt: 1 }}>
          Use placeholders like {"{{text1}}"}, {"{{text2}}"} in the title
          template. Example: "Score {"{{text1}}"} goals with {"{{text2}}"}{" "}
          players".
        </Alert>

        {selectedConfig && (
          <Alert
            severity={
              validatePlaceholders(details.titleTemplate, selectedConfig)
                ? "warning"
                : "success"
            }
            sx={{ mt: 1 }}
          >
            <strong>{details.objectiveType.replace(/_/g, " ")}</strong> requires{" "}
            {selectedConfig.minDynamicValues === selectedConfig.maxDynamicValues
              ? `exactly ${selectedConfig.minDynamicValues}`
              : `between ${selectedConfig.minDynamicValues} and ${selectedConfig.maxDynamicValues}`}{" "}
            dynamic value{selectedConfig.maxDynamicValues > 1 ? "s" : ""}. You
            must include{" "}
            {Array.from(
              { length: selectedConfig.minDynamicValues },
              (_, i) => `{{text${i + 1}}}`,
            ).join(", ")}{" "}
            {selectedConfig.minDynamicValues <
              selectedConfig.maxDynamicValues &&
              `(up to ${Array.from(
                { length: selectedConfig.maxDynamicValues },
                (_, i) => `{{text${i + 1}}}`,
              ).join(", ")})`}{" "}
            in the title template and in every language translation.
            {validatePlaceholders(details.titleTemplate, selectedConfig) && (
              <>
                <br />
                <strong>Current issue:</strong>{" "}
                {validatePlaceholders(details.titleTemplate, selectedConfig)}
              </>
            )}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            English title is used as the default fallback when a language
            translation is not available.
          </Typography>
        </Box>
      </Box>

      {isEdit && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              "& .MuiCard-root": {
                boxShadow: "none",
                border: "1px solid",
                borderColor: "divider",
                "&:hover": { boxShadow: "none" },
              },
            }}
          >
            <BulkTranslationUpload
              assetType="CHALLENGE_TITLE"
              assetId={id!}
              fields={[
                {
                  label: "Title Template",
                  fieldName: "titleTemplateTrans",
                  dummyText: "Keep {{text1}} Clean Sheets in a Row",
                },
              ]}
              onSuccess={fetchInfo}
            />
          </Box>
        </Box>
      )}

      <ViewLanguageModal
        isMultiline={false}
        label="Add Title Template Translation"
        isOpen={isOpen}
        close={close}
        onChange={handleModalChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default ObjectiveTypeTitleEditor;
